import { useState, useEffect, useCallback } from 'react'
import { getCommentsForPost } from '../data/blogComments'

const COMMENTS_STORAGE_KEY = 'ai4solar_blog_comments'
const LIKES_STORAGE_KEY = 'ai4solar_comment_likes'

export function useBlogComments(postId) {
  // Load initial comments from static data + localStorage additions
  const [comments, setComments] = useState(() => {
    const staticComments = getCommentsForPost(postId)
    try {
      const stored = localStorage.getItem(COMMENTS_STORAGE_KEY)
      const allUserComments = stored ? JSON.parse(stored) : {}
      const userCommentsForPost = allUserComments[postId] || []
      return [...staticComments, ...userCommentsForPost]
    } catch {
      return staticComments
    }
  })

  // Track which comments the user has liked
  const [likedComments, setLikedComments] = useState(() => {
    try {
      const stored = localStorage.getItem(LIKES_STORAGE_KEY)
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  })

  // Track like counts (for user-added likes on top of static counts)
  const [likeCounts, setLikeCounts] = useState(() => {
    try {
      const stored = localStorage.getItem(`${LIKES_STORAGE_KEY}_counts`)
      return stored ? JSON.parse(stored) : {}
    } catch {
      return {}
    }
  })

  // Persist liked comments to localStorage
  useEffect(() => {
    localStorage.setItem(LIKES_STORAGE_KEY, JSON.stringify(likedComments))
  }, [likedComments])

  // Persist like counts to localStorage
  useEffect(() => {
    localStorage.setItem(`${LIKES_STORAGE_KEY}_counts`, JSON.stringify(likeCounts))
  }, [likeCounts])

  // Check if user has liked a comment
  const hasLiked = useCallback((commentId) => {
    return likedComments.includes(commentId)
  }, [likedComments])

  // Get like count for a comment (static + user additions)
  const getLikeCount = useCallback((commentId, staticLikes = 0) => {
    const additionalLikes = likeCounts[commentId] || 0
    return staticLikes + additionalLikes
  }, [likeCounts])

  // Toggle like on a comment
  const toggleLike = useCallback((commentId) => {
    if (hasLiked(commentId)) {
      // Unlike
      setLikedComments(prev => prev.filter(id => id !== commentId))
      setLikeCounts(prev => ({
        ...prev,
        [commentId]: Math.max((prev[commentId] || 0) - 1, 0)
      }))
    } else {
      // Like
      setLikedComments(prev => [...prev, commentId])
      setLikeCounts(prev => ({
        ...prev,
        [commentId]: (prev[commentId] || 0) + 1
      }))
    }
  }, [hasLiked])

  // Add a new comment
  const addComment = useCallback((author, content) => {
    const newComment = {
      id: Date.now(),
      author,
      date: new Date().toISOString().split('T')[0],
      content,
      likes: 0,
      isUserComment: true
    }

    // Update local state
    setComments(prev => [...prev, newComment])

    // Persist to localStorage
    try {
      const stored = localStorage.getItem(COMMENTS_STORAGE_KEY)
      const allUserComments = stored ? JSON.parse(stored) : {}
      allUserComments[postId] = [...(allUserComments[postId] || []), newComment]
      localStorage.setItem(COMMENTS_STORAGE_KEY, JSON.stringify(allUserComments))
    } catch (e) {
      console.error('Failed to save comment:', e)
    }
  }, [postId])

  // Delete a user comment
  const deleteComment = useCallback((commentId) => {
    // Update local state
    setComments(prev => prev.filter(c => c.id !== commentId))

    // Remove from localStorage
    try {
      const stored = localStorage.getItem(COMMENTS_STORAGE_KEY)
      const allUserComments = stored ? JSON.parse(stored) : {}
      if (allUserComments[postId]) {
        allUserComments[postId] = allUserComments[postId].filter(c => c.id !== commentId)
        localStorage.setItem(COMMENTS_STORAGE_KEY, JSON.stringify(allUserComments))
      }
    } catch (e) {
      console.error('Failed to delete comment:', e)
    }
  }, [postId])

  return {
    comments,
    addComment,
    deleteComment,
    toggleLike,
    hasLiked,
    getLikeCount,
    commentCount: comments.length
  }
}
